import { z } from "zod";

import { BCP47LangCode } from "../i18n.types";

const AcceptLanguageEntrySchema = z.object({
  tag: z.string().min(1),
  quality: z.number().min(0).max(1),
});

type AcceptLanguageEntry = z.infer<typeof AcceptLanguageEntrySchema>;

const BASE_LANGUAGE_TO_LOCALE: Record<string, BCP47LangCode> = {
  en: "en-US",
  es: "es-MX",
  fr: "fr-CA",
  pt: "pt-BR",
};

const ENGLISH_COUNTRIES = new Set([
  "US",
  "GB",
  "AU",
  "NZ",
  "IE",
  "ZA",
  "JM",
  "TT",
  "BB",
  "GY",
  "BZ",
  "PH",
  "SG",
  "IN",
]);

const SPANISH_COUNTRIES = new Set([
  "MX",
  "AR",
  "BO",
  "CL",
  "CO",
  "CR",
  "CU",
  "DO",
  "EC",
  "SV",
  "GT",
  "HN",
  "NI",
  "PA",
  "PY",
  "PE",
  "PR",
  "UY",
  "VE",
  "ES",
]);

const FRENCH_COUNTRIES = new Set([
  "FR",
  "BE",
  "CH",
  "LU",
  "MC",
  "SN",
  "CI",
  "ML",
  "BF",
  "NE",
  "TG",
  "BJ",
  "GN",
  "CG",
  "CD",
  "CM",
  "GA",
  "MG",
  "HT",
]);

/**
 * Parses a raw Accept-Language header value into weighted entries sorted by
 * descending quality. Malformed entries are silently dropped.
 *
 * @example
 * parseAcceptLanguage("es-MX,es;q=0.9,en;q=0.8")
 * // [{ tag: "es-MX", quality: 1 }, { tag: "es", quality: 0.9 }, { tag: "en", quality: 0.8 }]
 */
export function parseAcceptLanguage(headerValue: string): AcceptLanguageEntry[] {
  if (!headerValue.trim()) return [];

  return headerValue
    .split(",")
    .map((segment) => {
      const parts = segment.trim().split(";");
      const tag = parts[0]?.trim() ?? "";
      const qualityPart = parts.find((p) => p.trim().startsWith("q="));
      const quality = qualityPart ? parseFloat(qualityPart.trim().slice(2)) : 1.0;

      const result = AcceptLanguageEntrySchema.safeParse({ tag, quality });
      return result.success ? result.data : null;
    })
    .filter((entry): entry is AcceptLanguageEntry => entry !== null && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality);
}

/**
 * Resolves a locale from an Accept-Language header value. Tries exact BCP47
 * match first, then falls back to base-language matching (e.g. `es` → `es-MX`,
 * `fr-FR` → `fr-CA`, `en-GB` → `en-US`).
 *
 * Returns `undefined` when no supported match is found.
 */
export function resolveLocaleFromHeader(
  headerValue: string | null,
  supportedLocales: readonly BCP47LangCode[],
): BCP47LangCode | undefined {
  if (!headerValue) return undefined;

  const entries = parseAcceptLanguage(headerValue);
  const supported = new Set(supportedLocales.map((l) => l.toLowerCase()));

  for (const { tag } of entries) {
    const lower = tag.toLowerCase();

    if (supported.has(lower)) {
      return supportedLocales.find((l) => l.toLowerCase() === lower);
    }

    const baseLang = lower.split("-")[0];
    const fallback = BASE_LANGUAGE_TO_LOCALE[baseLang];
    if (fallback && supportedLocales.includes(fallback)) {
      return fallback;
    }
  }

  return undefined;
}

/**
 * Maps a Vercel-provided country code (ISO 3166-1 alpha-2) and optional region
 * to the best supported locale. Returns `undefined` for unmapped countries so
 * the caller can fall through to the global default.
 *
 * Uses `x-vercel-ip-country` and `x-vercel-ip-country-region` header values.
 */
export function mapCountryToLocale(
  country: string,
  region?: string | null,
): BCP47LangCode | undefined {
  const upper = country.toUpperCase();

  if (upper === "CA") {
    return region?.toUpperCase() === "QC" ? "fr-CA" : "en-CA";
  }

  if (ENGLISH_COUNTRIES.has(upper)) return "en-US";
  if (SPANISH_COUNTRIES.has(upper)) return "es-MX";
  if (FRENCH_COUNTRIES.has(upper)) return "fr-CA";

  return undefined;
}
